/**
 * Browser localStorage caching utilities for Bihar athlete data
 * Provides instant page loads by caching data in the browser
 */

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const ATHLETE_CACHE_KEY = 'bihar_athletes_cache';
const INSIGHTS_CACHE_KEY = 'bihar_insights_cache';

/**
 * Save athlete data to browser cache
 */
export function cacheAthleteData(datasetId, athletes, stats) {
  try {
    const cacheData = {
      datasetId,
      athletes,
      stats,
      timestamp: Date.now()
    };
    localStorage.setItem(ATHLETE_CACHE_KEY, JSON.stringify(cacheData));
    console.log('✅ Cached athlete data for dataset:', datasetId);
  } catch (error) {
    console.warn('Failed to cache athlete data:', error);
    // If localStorage is full, clear old cache and retry
    if (error.name === 'QuotaExceededError') {
      localStorage.removeItem(ATHLETE_CACHE_KEY);
      localStorage.removeItem(INSIGHTS_CACHE_KEY);
    }
  }
}

/**
 * Get athlete data from browser cache
 * Returns null if cache is invalid or doesn't exist
 */
export function getCachedAthleteData(datasetId) {
  try {
    const cached = localStorage.getItem(ATHLETE_CACHE_KEY);
    if (!cached) {
      console.log('❌ No cached athlete data found');
      return null;
    }

    const { datasetId: cachedDatasetId, athletes, stats, timestamp } = JSON.parse(cached);
    
    // Check if cache is for the same dataset
    if (cachedDatasetId !== datasetId) {
      console.log('❌ Cached data is for different dataset');
      return null;
    }

    // Check if cache is still valid
    const age = Date.now() - timestamp;
    if (age > CACHE_DURATION) {
      console.log('❌ Cached data expired (age:', Math.round(age / 1000), 'seconds)');
      return null;
    }

    console.log('✅ Using cached athlete data (age:', Math.round(age / 1000), 'seconds)');
    return { athletes, stats };
  } catch (error) {
    console.warn('Failed to read cached athlete data:', error);
    return null;
  }
}

/**
 * Save insights data to browser cache
 */
export function cacheInsightsData(datasetId, insights) {
  try {
    const cacheData = {
      datasetId,
      insights,
      timestamp: Date.now()
    };
    localStorage.setItem(INSIGHTS_CACHE_KEY, JSON.stringify(cacheData));
    console.log('✅ Cached insights data for dataset:', datasetId);
  } catch (error) {
    console.warn('Failed to cache insights data:', error);
    if (error.name === 'QuotaExceededError') {
      localStorage.removeItem(ATHLETE_CACHE_KEY);
      localStorage.removeItem(INSIGHTS_CACHE_KEY);
    }
  }
}

/**
 * Get insights data from browser cache
 * Returns null if cache is invalid or doesn't exist
 */
export function getCachedInsightsData(datasetId) {
  try {
    const cached = localStorage.getItem(INSIGHTS_CACHE_KEY);
    if (!cached) {
      console.log('❌ No cached insights data found');
      return null;
    }

    const { datasetId: cachedDatasetId, insights, timestamp } = JSON.parse(cached);
    
    // Check if cache is for the same dataset
    if (cachedDatasetId !== datasetId) {
      console.log('❌ Cached insights is for different dataset');
      return null;
    }

    // Check if cache is still valid
    const age = Date.now() - timestamp;
    if (age > CACHE_DURATION) {
      console.log('❌ Cached insights expired (age:', Math.round(age / 1000), 'seconds)');
      return null;
    }

    console.log('✅ Using cached insights data (age:', Math.round(age / 1000), 'seconds)');
    return insights;
  } catch (error) {
    console.warn('Failed to read cached insights data:', error);
    return null;
  }
}

/**
 * Clear all Bihar caches
 * Useful when importing new data
 */
export function clearBiharCache() {
  localStorage.removeItem(ATHLETE_CACHE_KEY);
  localStorage.removeItem(INSIGHTS_CACHE_KEY);
  console.log('✅ Cleared all Bihar caches');
}

/**
 * Get cache info for debugging
 */
export function getCacheInfo() {
  const athleteCache = localStorage.getItem(ATHLETE_CACHE_KEY);
  const insightsCache = localStorage.getItem(INSIGHTS_CACHE_KEY);
  
  const info = {
    athleteCache: null,
    insightsCache: null,
    totalSize: 0
  };

  if (athleteCache) {
    const data = JSON.parse(athleteCache);
    const age = Date.now() - data.timestamp;
    info.athleteCache = {
      datasetId: data.datasetId,
      athleteCount: data.athletes?.length || 0,
      ageSeconds: Math.round(age / 1000),
      sizeKB: Math.round(athleteCache.length / 1024),
      valid: age < CACHE_DURATION
    };
    info.totalSize += athleteCache.length;
  }

  if (insightsCache) {
    const data = JSON.parse(insightsCache);
    const age = Date.now() - data.timestamp;
    info.insightsCache = {
      datasetId: data.datasetId,
      ageSeconds: Math.round(age / 1000),
      sizeKB: Math.round(insightsCache.length / 1024),
      valid: age < CACHE_DURATION
    };
    info.totalSize += insightsCache.length;
  }

  info.totalSizeKB = Math.round(info.totalSize / 1024);
  info.totalSizeMB = (info.totalSize / (1024 * 1024)).toFixed(2);

  return info;
}
